
import { getCurrentConference } from '../base/conference';
import { addTileGradients } from '../base/environment/environment';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import {
    IS_RECORDING_AND_LIVESTREAMING_ON,
    SEND_AUTO_RECORD_COMMAND,
    autoRecordStop,
    recordingandLivestreamingOn
} from '../base/mtApi';
import {
    PARTICIPANT_UPDATED,
    SET_PARTICIPANT_COLOR,
    getSharedVideoParticipant,
    isOwnColorReport,
    isSharedThumbnailEnabled,
    participantUpdated,
    sendLocalParticipantColor,
    setParticipantColor
} from '../base/participants';
import { StateListenerRegistry, equals } from '../base/redux';
import { clientResized } from '../base/responsive-ui';
import { hasActivePriorityContent } from '../conference/recorderViewHelpers';
import { setFilmstripVisible } from '../filmstrip/actions';
import { getNotesPaneOpen } from '../notes/functions';
import { getParticipantsPaneOpen } from '../participants-pane/functions';
import { isVideoPlaying } from '../shared-video/functions';
import { isFixedCaptionsVisible } from '../subtitles/functions.web';
import { setOverflowDrawer } from '../toolbox/actions.web';
import {
    LAYOUTS,
    getCurrentLayout,
    getTileViewGridDimensions,
    shouldDisplayTileView,
    toggleTileView
} from '../video-layout';
import { iAmVisitor } from '../visitors/functions';
import {
    getWebinarAudienceRemoteParticipants,
    isAudienceLayoutActive
} from '../webinar/functions';

import {
    setHorizontalViewDimensions,
    setTileViewDimensions
} from './actions.web';
import {
    ASPECT_RATIO_BREAKPOINT,
    DISPLAY_DRAWER_THRESHOLD,
    SINGLE_COLUMN_BREAKPOINT,
    TWO_COLUMN_BREAKPOINT
} from './constants';
import { resizeThumbnailsForVerticalView } from './functions.web';

declare var APP: Object;

/**
 * Listens for changes in the number of participants to calculate the dimensions of the tile view grid and the tiles.
 */
StateListenerRegistry.register(
    /* selector */ state => {
        // Track the very list the grid is sized from (getTileViewGridDimensions
        // counts getWebinarAudienceRemoteParticipants), not the raw participant
        // count. Hiding an occupant or granting a webinar role changes that list
        // without changing the count, so keying on the count left the grid sized
        // for the old list: phantom slots, and thumbnails shifted into the wrong
        // cells until an unrelated join or leave resized it.
        const ids = getWebinarAudienceRemoteParticipants(state)
            .map(participant => participant.id)
            .join('|');

        const showShared = !isAudienceLayoutActive(state);
        const sharedWhiteboard = showShared && Boolean(isSharedThumbnailEnabled(state));
        const sharedVideo = showShared && Boolean(
            isVideoPlaying(state)
            || getSharedVideoParticipant(state)
            || state['features/shared-video']?.ownerId
        );

        // The local tile is a grid slot too, and visitors never get one, so a
        // promotion has to resize the grid even when the remote list is identical.
        return `${isAudienceLayoutActive(state) ? 'audience' : 'all'}:${iAmVisitor(state)}:${sharedWhiteboard}:${sharedVideo}:${ids}`;
    },
    /* listener */ (_, store) => {
        const state = store.getState();

        if (shouldDisplayTileView(state)) {
            const gridDimensions = getTileViewGridDimensions(state);
            const oldGridDimensions
        = state['features/filmstrip'].tileViewDimensions.gridDimensions;

            if (!equals(gridDimensions, oldGridDimensions)) {
                const { clientHeight, clientWidth }
          = state['features/base/responsive-ui'];

                store.dispatch(
          setTileViewDimensions(
            gridDimensions,
            {
                clientHeight,
                clientWidth
            },
            store
          )
                );
            }
        }
    }
);

/**
 * Listens for changes in the number of participants to calculate the dimensions of the tile view grid and the tiles.
 */
StateListenerRegistry.register(
    /* selector */ state => state['features/base/participants'].length,
    /* listener */ (numberOfParticipants, store) => {
        const state = store.getState();
        const preregData = state['features/base/mtApi']?.pregData;
        const { iAmRecorder } = state['features/base/config'] || {};

        const desktopTracks
      = state['features/base/tracks']?.length > 0
          ? state['features/base/tracks'].find(it => it.videoType === 'desktop')
          : null;

        // Update tile view classes for all SmallVideo instances when participants count changes
        if (typeof APP !== 'undefined' && APP.UI && APP.UI.VideoLayout) {
            APP.UI.VideoLayout.onParticipantsOrViewStateChange();
        }

        if (iAmRecorder) {
            if (
                numberOfParticipants > 2

            && preregData?.set_recording_tile_view
            && !isVideoPlaying(state)
            && !desktopTracks
            && !hasActivePriorityContent(state)
            && numberOfParticipants < (parseInt(preregData?.max_recording_tiles, 10) ?? 20)
            ) {
                setTimeout(() => {
                    if (
                        store.getState()['features/video-layout'].tileViewEnabled
                  !== true
                || getCurrentLayout(store.getState())
                  === LAYOUTS.VERTICAL_FILMSTRIP_VIEW
                    ) {
                        store.dispatch(toggleTileView(true));
                    }
                }, 2000);


            } else if (
                !preregData?.set_recording_tile_view
            || numberOfParticipants > (parseInt(preregData?.max_recording_tiles, 10) ?? 20)
            ) {
                setTimeout(() => {
                    if (
                        store.getState()['features/video-layout'].tileViewEnabled
                  === true
                || getCurrentLayout(store.getState()) === LAYOUTS.TILE_VIEW
                    ) {
                        store.dispatch(toggleTileView(false));
                    }
                }, 2000);
            }
        }
    }

);

/**
 * Listens for changes in conference view to update tile view classes immediately.
 */
StateListenerRegistry.register(
    /* selector */ state => state['features/base/conference'].conferenceView,
    /* listener */ () => {
        // Update tile view classes for all SmallVideo instances when conference view changes
        if (typeof APP !== 'undefined' && APP.UI && APP.UI.VideoLayout) {
            APP.UI.VideoLayout.onParticipantsOrViewStateChange();
        }
    }
);

let _webAutoRecordHandler = null;
let _webRecordingLivestreamHandler = null;

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g. Clear messages or close the chat modal if it's left
 * open.
 */
StateListenerRegistry.register(
  state => getCurrentConference(state),
  (conference, store, previousConference) => {
      if (previousConference) {
          if (_webAutoRecordHandler) {
              previousConference.removeCommandListener(
                  SEND_AUTO_RECORD_COMMAND,
                  _webAutoRecordHandler
              );
              _webAutoRecordHandler = null;
          }
          if (_webRecordingLivestreamHandler) {
              previousConference.removeCommandListener(
                  IS_RECORDING_AND_LIVESTREAMING_ON,
                  _webRecordingLivestreamHandler
              );
              _webRecordingLivestreamHandler = null;
          }
      }
      if (conference && conference !== previousConference) {
          _webAutoRecordHandler = ({ value }) => {
              const { dispatch } = store;

              if (value) {
                  dispatch(autoRecordStop(value));
              }
          };
          conference.addCommandListener(SEND_AUTO_RECORD_COMMAND, _webAutoRecordHandler);

          _webRecordingLivestreamHandler = ({ value }) => {
              const { dispatch } = store;

              if (value) {
                  dispatch(recordingandLivestreamingOn(value));
              }
          };
          conference.addCommandListener(
              IS_RECORDING_AND_LIVESTREAMING_ON,
              _webRecordingLivestreamHandler
          );
      }
  }
);

/**
 * Listens for changes in the selected layout to calculate the dimensions of the tile view grid and horizontal view.
 */
StateListenerRegistry.register(
    /* selector */ state => getCurrentLayout(state),
    /* listener */ (layout, store) => {
        const state = store.getState();

        switch (layout) {
        case LAYOUTS.TILE_VIEW: {
            const { clientHeight, clientWidth }
          = state['features/base/responsive-ui'];

            store.dispatch(
          setTileViewDimensions(
            getTileViewGridDimensions(state),
            {
                clientHeight,
                clientWidth
            },
            store
          )
            );
            break;
        }
        case LAYOUTS.HORIZONTAL_FILMSTRIP_VIEW:
            store.dispatch(
          setHorizontalViewDimensions(
            state['features/base/responsive-ui'].clientHeight
          )
            );
            break;
        case LAYOUTS.VERTICAL_FILMSTRIP_VIEW:
            resizeThumbnailsForVerticalView();
            break;
        }
    }
);

/**
 * Listens for changes in the chat state to calculate the dimensions of the tile view grid and the tiles.
 */
StateListenerRegistry.register(
    /* selector */ state => state['features/chat'].isOpen,
    /* listener */ (isChatOpen, store) => {
        const state = store.getState();

        if (isChatOpen) {
            // $FlowFixMe
            document.body.classList.add('shift-right');
        } else {
            // $FlowFixMe
            document.body.classList.remove('shift-right');
        }

        if (shouldDisplayTileView(state)) {
            const gridDimensions = getTileViewGridDimensions(state);
            const { clientHeight, clientWidth }
        = state['features/base/responsive-ui'];

            store.dispatch(
        setTileViewDimensions(
          gridDimensions,
          {
              clientHeight,
              clientWidth
          },
          store
        )
            );
        }
    }
);

/**
 * Listens for changes in the participant pane state to calculate the
 * dimensions of the tile view grid and the tiles.
 */
StateListenerRegistry.register(
    /* selector */ state => getParticipantsPaneOpen(state) || getNotesPaneOpen(state),
    /* listener */ (isOpen, store) => {
        const { innerWidth, innerHeight } = window;

        store.dispatch(clientResized(innerWidth, innerHeight));
    }
);

/**
 * Listens for changes in the client width to determine whether the overflow menu(s) should be displayed as drawers.
 */
StateListenerRegistry.register(
    /* selector */ state =>
        state['features/base/responsive-ui'].clientWidth < DISPLAY_DRAWER_THRESHOLD,
    /* listener */ (widthBelowThreshold, store) => {
        store.dispatch(setOverflowDrawer(widthBelowThreshold));
    }
);

/**
 * Gracefully hide/show the filmstrip when going past threshold.
 */
StateListenerRegistry.register(
    /* selector */ state =>
        state['features/base/responsive-ui'].clientWidth < ASPECT_RATIO_BREAKPOINT,
    /* listener */ (widthBelowThreshold, store) => {
        store.dispatch(setFilmstripVisible(!widthBelowThreshold));
    }
);

/**
 * Symbol mapping used for the tile view responsiveness computation.
 */
const responsiveColumnMapping = {
    multipleColumns: Symbol('multipleColumns'),
    singleColumn: Symbol('singleColumn'),
    twoColumns: Symbol('twoColumns'),
    twoParticipantsSingleColumn: Symbol('twoParticipantsSingleColumn')
};

/**
 * Listens for changes in the screen size to recompute
 * the dimensions of the tile view grid and the tiles for responsiveness.
 */
StateListenerRegistry.register(
    /* selector */ state => {
        const { clientWidth } = state['features/base/responsive-ui'];

        if (
            clientWidth < TWO_COLUMN_BREAKPOINT
      && clientWidth >= ASPECT_RATIO_BREAKPOINT
        ) {
            // Forcing the recomputation of tiles when screen switches in or out of
            // the (TWO_COLUMN_BREAKPOINT, ASPECT_RATIO_BREAKPOINT] interval.
            return responsiveColumnMapping.twoColumns;
        } else if (
            clientWidth < ASPECT_RATIO_BREAKPOINT
      && clientWidth >= SINGLE_COLUMN_BREAKPOINT
        ) {
            // Forcing the recomputation of tiles when screen switches in or out of
            // the (ASPECT_RATIO_BREAKPOINT, SINGLE_COLUMN_BREAKPOINT] interval.
            return responsiveColumnMapping.twoParticipantsSingleColumn;
        } else if (clientWidth < SINGLE_COLUMN_BREAKPOINT) {
            // Forcing the recomputation of tiles when screen switches below SINGLE_COLUMN_BREAKPOINT.
            return responsiveColumnMapping.singleColumn;
        }

        // Forcing the recomputation of tiles when screen switches above TWO_COLUMN_BREAKPOINT.
        return responsiveColumnMapping.multipleColumns;
    },
    /* listener */ (_, store) => {
        const state = store.getState();

        if (shouldDisplayTileView(state)) {
            const gridDimensions = getTileViewGridDimensions(state);
            const { clientHeight, clientWidth }
        = state['features/base/responsive-ui'];

            store.dispatch(
        setTileViewDimensions(
          gridDimensions,
          {
              clientHeight,
              clientWidth
          },
          store
        )
            );
        }
    }
);

let _webFilmstripReceiveMessage = null;

StateListenerRegistry.register(
  state => getCurrentConference(state),
  (conference, store, previousConference) => {
      if (previousConference && _webFilmstripReceiveMessage) {
          previousConference.off(
              MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
              _webFilmstripReceiveMessage
          );
          previousConference.off(
              MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
              _webFilmstripReceiveMessage
          );
          _webFilmstripReceiveMessage = null;
      }
      if (conference && conference !== previousConference) {
          const receiveMessage = (sender, data) => {
              switch (data.type) {
              case SET_PARTICIPANT_COLOR: {
                  const { participantId, color } = data;

                  if (participantId && color && isOwnColorReport(sender, data)) {
                      store.dispatch(setParticipantColor(participantId, addTileGradients(color)));
                  }
                  break;
              }

              case 'REQUEST_PARTICIPANT_COLORS': {
                  // A recorder asking for colors gets only ours, sent to it alone.
                  sendLocalParticipantColor(store.getState(), typeof sender === 'string' ? sender : sender?.getId?.());
                  break;
              }

              case PARTICIPANT_UPDATED: {
                  const { participantId } = data;

                  store.dispatch(
              participantUpdated({
                  id: participantId,
                  raisedHand: false
              })
                  );
                  break;
              }
              }
          };

          _webFilmstripReceiveMessage = receiveMessage;

          conference.on(
        MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
        receiveMessage
          );
          conference.on(
        MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
        receiveMessage
          );
      }
  }
);

/**
 * Resets the carousel start index when switching layouts or when tile view tile limit changes.
 */
StateListenerRegistry.register(
    /* selector */ state => {
        return {
            audienceParticipants: isAudienceLayoutActive(state)
                ? getWebinarAudienceRemoteParticipants(state)
                    .map(participant => participant.id)
                    .join('|')
                : undefined,
            layout: getCurrentLayout(state),
            tileViewTileLimit: state['features/filmstrip']?.tileViewTileLimit
        };
    },
    /* listener */ (current, store, previous) => {
        // Reset carousel index when switching to/from tile view or when tile limit changes
        if (current.layout !== previous?.layout
                || current.tileViewTileLimit !== previous?.tileViewTileLimit
                || current.audienceParticipants !== previous?.audienceParticipants) {
            const { setVisibleParticipantsStartIndex } = require('./actions.web');

            store.dispatch(setVisibleParticipantsStartIndex(0));
        }
    },

    // The selector builds a new value every time; compare by content so the listener runs only on a change
    // instead of on every redux action.
    {
        deepEquals: true
    }
);

/**
 * Re-fits the tile grid when the docked captions panel appears or goes away,
 * since it takes height from the tiles.
 */
StateListenerRegistry.register(
    /* selector */ state => isFixedCaptionsVisible(state),
    /* listener */ (_, store) => {
        const state = store.getState();

        if (shouldDisplayTileView(state)) {
            const { clientHeight, clientWidth } = state['features/base/responsive-ui'];

            store.dispatch(setTileViewDimensions(getTileViewGridDimensions(state), {
                clientHeight,
                clientWidth
            }, store));
        }
    }
);
